import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Input from '../../../../../shared/_TBComponents/Input';
import Modal from '../../../../../shared/_TBComponents/Modal';
import SelectBox from '../../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../../shared/_TBComponents/Text';
import { showSuccessToast, showErrorToast } from '../../../../../shared/utils/toastManager';
import {
  createCalendarEntry, createChildCalendarEntry, updateCalendarEntry, updateChildCalendarEntry,
} from '../../../../calendars/ducks/calendarsActions';
import { Row, FieldWrapper } from '../forms/UserProfileFields';
import ConfirmModal from './ConfirmModal';

const WORKOUT_VISIBILITY_OPTIONS = [
  { id: 0, name: 'All' },
  { id: 1, name: 'One Week' },
  { id: 2, name: 'Current Day' },
];

const SELECTBOX_STYLES = {
  control: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0',
    boxShadow: 'none',
    '&:hover': { borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0' },
  }),
  option: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '14px',
    fontWeight: 600,
    backgroundColor: state.isFocused || state.isSelected ? 'rgba(119, 167, 255, 0.15)' : 'transparent',
    color: '#333333',
    '&:active': { backgroundColor: 'rgba(119, 167, 255, 0.25)' },
  }),
};

const FormWrapper = styled.div`
  width: min(600px, 100vw);
  min-width: min(500px, 100vw);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;

  @media screen and (max-width: 767px) {
    width: 100vw;
    min-width: unset;
  }
`;

const FormLayout = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-bottom: 0 !important;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 0 12px 20px;
  flex-shrink: 0;
`;

const FormBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 24px;

  @media (max-width: 767px) {
    padding: 12px 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-shrink: 0;
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #EEEEEE;
  box-sizing: border-box;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
  }
`;

// Prevent nesting a top-level calendar that already has children
const isParentPickerDisabled = (calendar) => (
  !!calendar && !calendar.parentId && (calendar.childCalendars || []).length > 0
);

const AddCalendarModal = ({
  isOpen, onClose, calendar, onCreated, onUpdated,
}) => {
  const isEditMode = !!calendar;

  const dispatch = useDispatch();
  const formikDirtyRef = useRef(false);
  const scrollRef = useRef(null);

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const calendarsRaw = useSelector((state) => state.calendars.data.unparsedCalendars);
  const calAccess = useSelector((state) => state.auth.data.currentUser?.calAccess);
  const isPrimaryAdmin = useSelector((state) => state.auth.data.currentUser?.primaryAdmin);

  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleCloseAttempt = () => {
    if (formikDirtyRef.current) {
      setUnsavedModalOpen(true);
    } else {
      onClose();
    }
  };

  // Only primary admins or coaches with all calendars can change a calendar's parent.
  const hasFullCalendarAccess = Boolean(isPrimaryAdmin) || calAccess === null;

  // Active top-level calendars only, not the placeholder for a parent the coach can't see.
  const parentOptions = calendarsRaw
    .filter((c) => c.id != null && !c.parentId && !c.archived && c.id !== calendar?.id)
    .map((c) => ({ id: c.id, name: c.name }));

  // Keeps a hidden parent's id so saving doesn't move the calendar.
  const getInitialParent = () => {
    if (!calendar?.parentId) return null;
    return parentOptions.find((o) => o.id === calendar.parentId)
      || { id: calendar.parentId, name: 'Parent calendar (no access)' };
  };

  const initialValues = isEditMode
    ? {
      name: calendar.name || '',
      parentCalendar: getInitialParent(),
      workoutVisibility: WORKOUT_VISIBILITY_OPTIONS.find(
        (o) => o.id === calendar.workoutVisibility,
      ) || WORKOUT_VISIBILITY_OPTIONS[0],
    }
    : {
      name: '',
      parentCalendar: null,
      workoutVisibility: WORKOUT_VISIBILITY_OPTIONS[0],
    };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Calendar name is required'),
  });

  const handleFormSubmit = (values, { setSubmitting }) => {
    const body = {
      name: values.name,
      workoutVisibility: values.workoutVisibility.id,
      parentId: values.parentCalendar?.id || 0,
    };

    const callbacks = {
      onSuccess: () => {
        showSuccessToast(`${values.name} has been ${isEditMode ? 'updated' : 'added'}.`);
        setSubmitting(false);
        setIsSaving(false);
        if (isEditMode && onUpdated) onUpdated();
        if (!isEditMode && onCreated) onCreated();
        onClose();
      },
      onError: () => {
        showErrorToast(`Unable to ${isEditMode ? 'update' : 'add'} calendar. Please try again.`);
        setSubmitting(false);
        setIsSaving(false);
      },
    };

    setIsSaving(true);

    if (isEditMode) {
      // Use the calendar's current parent to determine the update endpoint
      if (calendar.parentId) {
        dispatch(
          updateChildCalendarEntry(accountCode, calendar.parentId, calendar.id, body, callbacks),
        );
      } else {
        dispatch(updateCalendarEntry(accountCode, calendar.id, body, callbacks));
      }
      return;
    }

    if (values.parentCalendar) {
      dispatch(createChildCalendarEntry(accountCode, values.parentCalendar.id, body, callbacks));
    } else {
      dispatch(createCalendarEntry(accountCode, body, callbacks));
    }
  };

  const title = isEditMode ? calendar.name : 'New Calendar';
  const saveLabel = isEditMode ? 'Save Changes' : 'Save';

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleCloseAttempt}
        customBorderRadius='8px'
        customPadding='0px'
        customMaxWidth='600px'
        customMinWidth='min(500px, 100vw)'
        customButton
        widthFitContent
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({
            isSubmitting, values, errors, touched, handleChange, handleBlur,
            setFieldValue, dirty,
          }) => {
            formikDirtyRef.current = dirty;
            const hasSubCalendars = isEditMode && isParentPickerDisabled(calendar);
            const parentLocked = hasSubCalendars || (isEditMode && !hasFullCalendarAccess);

            return (
              <FormWrapper>
                <FormLayout autoComplete='off'>
                  <Header>
                    <Text styledTextProps={{ fontSize: '24px', fontWeight: 700, color: '#1A1A1A' }}>
                      {title}
                    </Text>
                    <Button
                      icon='remove'
                      iconSize={15}
                      onClick={(e) => { e.preventDefault(); handleCloseAttempt(); }}
                      fontSize='13px'
                      txtColor='#9E9E9E'
                      noBorder
                    />
                  </Header>

                  <FormBody ref={scrollRef}>
                    <Row>
                      <FieldWrapper>
                        <Input
                          label='Calendar Name'
                          name='name'
                          type='text'
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Calendar Name'
                          fontSize='14px'
                          height='38px'
                          width='100%'
                          styledPropsLabel={{ fontWeight: 700 }}
                          error={!!(touched.name && errors.name)}
                          errorMsg={errors.name || ''}
                          right='5px'
                          top='38px'
                        />
                      </FieldWrapper>
                    </Row>

                    <Row>
                      <FieldWrapper>
                        <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
                          Parent Calendar
                        </Text>
                        <SelectBox
                          options={parentOptions}
                          value={values.parentCalendar}
                          onChangeFunction={(opt) => setFieldValue('parentCalendar', opt)}
                          placeholder='No parent (top-level calendar)'
                          isClearable
                          isDisabled={parentLocked}
                          customStyles={SELECTBOX_STYLES}
                          menuPortalTarget={document.body}
                          onMenuOpen={() => {
                            if (scrollRef?.current) scrollRef.current.style.overflowY = 'hidden';
                          }}
                          onMenuClose={() => {
                            if (scrollRef?.current) scrollRef.current.style.overflowY = 'auto';
                          }}
                        />
                        {parentLocked && (
                          <Text
                            styledTextProps={{ fontSize: '13px', color: '#9E9E9E', marginTop: '6px' }}
                          >
                            {hasSubCalendars
                              ? 'This calendar has sub-calendars, so it can\'t be moved under another calendar.'
                              : 'Only coaches with access to all calendars can change the parent calendar.'}
                          </Text>
                        )}
                      </FieldWrapper>
                    </Row>

                    <Row>
                      <FieldWrapper>
                        <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
                          Workout Visibility
                        </Text>
                        <SelectBox
                          options={WORKOUT_VISIBILITY_OPTIONS}
                          value={values.workoutVisibility}
                          onChangeFunction={(opt) => setFieldValue('workoutVisibility', opt)}
                          isClearable={false}
                          customStyles={SELECTBOX_STYLES}
                          menuPortalTarget={document.body}
                          onMenuOpen={() => {
                            if (scrollRef?.current) scrollRef.current.style.overflowY = 'hidden';
                          }}
                          onMenuClose={() => {
                            if (scrollRef?.current) scrollRef.current.style.overflowY = 'auto';
                          }}
                        />
                      </FieldWrapper>
                    </Row>
                  </FormBody>

                  <ButtonContainer>
                    <Button
                      type='submit'
                      name={isSubmitting || isSaving ? 'Saving...' : saveLabel}
                      fontSize='14px'
                      txtColor='#FFF'
                      bgColor='#1A1A1A'
                      borderColor='#1A1A1A'
                      customBorderRadius='6px'
                      styledTextProps={{ fontWeight: 600 }}
                      disabled={isSubmitting || isSaving}
                    />
                  </ButtonContainer>
                </FormLayout>
              </FormWrapper>
            );
          }}
        </Formik>
      </Modal>

      <ConfirmModal
        isOpen={unsavedModalOpen}
        onClose={() => setUnsavedModalOpen(false)}
        onConfirm={() => { setUnsavedModalOpen(false); onClose(); }}
        title='Unsaved Changes'
        message='You have unsaved changes. Are you sure you want to leave?'
        confirmLabel='Leave'
        confirmIcon={null}
      />
    </>
  );
};

AddCalendarModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  calendar: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    parentId: PropTypes.number,
    workoutVisibility: PropTypes.number,
    childCalendars: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onCreated: PropTypes.func,
  onUpdated: PropTypes.func,
};

AddCalendarModal.defaultProps = {
  calendar: null,
  onCreated: null,
  onUpdated: null,
};

export default AddCalendarModal;
